import { useGetOrgObsJobRisks } from "openapi/api/endpoints"
import { JobRisk } from "openapi/api/model"

export type OwnerViewData = {
    orgMetrics?: {
        openReqs?: number
        avgTimeToFill?: number
        riskJobs?: number
        teamPerformance?: number
    }

    riskJobs?: {
        id?: string
        job?: string
        risk?: "low" | "medium" | "high" | "none"
        days?: number
        reason?: string
    }[]

    leaderboard?: {
        rank?: number
        name?: string
        xp?: number
        hires?: number
        badge?: string
    }[]

    teamMembersPipelines: {
        id?: string
        name?: string
        jobs?: {
            id?: string
            title?: string
            status?: string
            longlist?: number
            interviewing?: number
            hired?: number
        }[]
    }[]
}

export type JobHealthStatus =
    | "healthy"
    | "stalled"
    | "at_risk"
    | "exhausted"
    | "filled"
    | "unknown"

export interface JobHealthMeta {
    riskLevel: "low" | "medium" | "high" | "none"
    label: string
    shortExplanation: string
}

/**
 * Maps a job health status to a short UI-friendly meta object.
 */
export function getJobHealthMeta(status: JobHealthStatus): JobHealthMeta {
    switch (status) {
        case "healthy":
            return {
                riskLevel: "low",
                label: "on track",
                shortExplanation: "Active pipeline; steady progress",
            }

        case "stalled":
            return {
                riskLevel: "medium",
                label: "medium risk",
                shortExplanation: "Slow progress; follow up needed",
            }

        case "at_risk":
            return {
                riskLevel: "high",
                label: "high risk",
                shortExplanation: "No recent activity; job going cold",
            }

        case "exhausted":
            return {
                riskLevel: "high",
                label: "high risk",
                shortExplanation: "No qualified candidates moving forward",
            }

        case "filled":
            return {
                riskLevel: "none",
                label: "filled",
                shortExplanation: "Search completed",
            }

        default:
            return {
                riskLevel: "none",
                label: "unknown",
                shortExplanation: "Status unavailable",
            }
    }
}


function getAgeInDays(job_created_at: string): number {
    const createdDate = new Date(job_created_at)
    const now = new Date()
    const diffMs = now.getTime() - createdDate.getTime() // difference in milliseconds
    
    const diffDays = diffMs / (1000 * 60 * 60 * 24) // convert to days

    // round or floor depending on what you want
    return Math.floor(diffDays)
}

function getAverageAgeInDays(items?: JobRisk[]): number {
    if (items?.length === 0) return 0
    const totalDays = items?.reduce((sum, item) => sum + getAgeInDays(item.job_created_at??''), 0)
    const age= totalDays??0 / (items?.length as number)
    return age
}

type RecruiterGroup = {
    id: string
    name: string
    jobs: {
        id: string
        title: string
        status: string
        longlist: number
        interviewing: number
        hired: number
    }[]
}

function groupJobsByRecruiter(jobs: JobRisk[]): RecruiterGroup[] | undefined {
    const grouped = new Map<string, RecruiterGroup>()
    if(!jobs?.length)return
    for (const job of jobs) {
        const id = job.created_by
        const name = job.recruiter_name ?? "Unknown"

        if (!grouped.has(id)) {
            grouped.set(id, { id, name, jobs: [] })
        }

        grouped.get(id)!.jobs.push({
            id: job.job_id,
            title: job.title,
            status: job.status,
            longlist: job.longlist_count ?? 0,
            interviewing: job.interviewing_count ?? 0,
            hired: job.hired_count ?? 0,
        })
    }

    return Array.from(grouped.values())
}



export const useOwnerData = () => {
    const { data, isLoading } = useGetOrgObsJobRisks()
    const ownerView: OwnerViewData = {
        riskJobs: data?.map((item) => {
            return {
                id: item.job_id as string,
                days: getAgeInDays(item.job_created_at??'')??0,
                job: item.title,
                reason: getJobHealthMeta(item.health_status as JobHealthStatus).shortExplanation,
                risk: getJobHealthMeta(item.health_status as JobHealthStatus).riskLevel
            }
        }).filter((job) => job.risk !== 'none') as OwnerViewData['riskJobs'],
        teamMembersPipelines: groupJobsByRecruiter(data as JobRisk[]) as RecruiterGroup[]
    };

    ownerView.orgMetrics = ({
        openReqs: data?.length,
        avgTimeToFill: getAverageAgeInDays(data as JobRisk[]),
        riskJobs: ownerView.riskJobs?.length
    }) as OwnerViewData['orgMetrics'];

    ownerView.leaderboard = ([
        { rank: 1, name: "Sarah Johnson", xp: 2450, hires: 8, badge: "🥇" },
        { rank: 2, name: "Mike Chen", xp: 2380, hires: 7, badge: "🥈" },
        { rank: 3, name: "Alex Rivera", xp: 2200, hires: 6, badge: "🥉" },
        { rank: 4, name: "You", xp: 1950, hires: 5, badge: "" },
        { rank: 5, name: "Emma Davis", xp: 1800, hires: 4, badge: "" },
    ]) as OwnerViewData['leaderboard'] 

    

    return {
        ownerView,
        isLoading

    }

}